/**
 * In-Presentation Search Agent - System Prompt Builder
 * 
 * Assembles the 5-section system prompt for the In-Presentation Search agent.
 */

import { SystemPromptBuilder } from '../../../base/systemPrompt';
import { ResolvedContextItem } from '../../../../core/types';

import { getIntroSection, IN_PRESENTATION_INTRO } from './intro';
import { buildToolsSection } from './tools';
import { getSuggestionsSection, IN_PRESENTATION_SUGGESTIONS } from './suggestions';
import { getContextNoteSection, IN_PRESENTATION_CONTEXT_NOTE } from './contextNote';
import { getEvaluationSection, IN_PRESENTATION_EVALUATION } from './evaluation';

export interface InPresentationPromptParams {
    resolvedContext?: ResolvedContextItem[];
    deckRegistry?: Map<number, { id: string; title: string; slidesReady: number; totalSlides: number }>;
    tools?: any[];
}

/**
 * Build the complete system prompt for the In-Presentation Search agent.
 * Uses Keywords AI prompts if available, with code fallbacks.
 */
export function buildInPresentationSystemPrompt(params: InPresentationPromptParams = {}): string {
    const hasContext = params.resolvedContext && params.resolvedContext.length > 0;
    
    const builder = new SystemPromptBuilder();
    
    let prompt = builder.build({
        intro: getIntroSection(),
        tools: buildToolsSection(params.tools || []),
        suggestions: getSuggestionsSection(),
        contextNote: getContextNoteSection(hasContext, params.deckRegistry),
        evaluation: getEvaluationSection(),
    });
    
    // Append resolved context if available
    if (hasContext && params.resolvedContext) {
        const contextDescriptions = params.resolvedContext.map(item => {
            const ids = Array.from(item.resolved.keys()).join(', ');
            return `- ${item.original.purpose} (IDs: ${ids})`;
        }).join('\n');
        prompt += `\n\n## Available Context\n${contextDescriptions}`;
    }
    
    return prompt;
}

// Export all sections for admin panel access
export {
    getIntroSection,
    buildToolsSection,
    getSuggestionsSection,
    getContextNoteSection,
    getEvaluationSection,
    IN_PRESENTATION_INTRO,
    IN_PRESENTATION_SUGGESTIONS,
    IN_PRESENTATION_CONTEXT_NOTE,
    IN_PRESENTATION_EVALUATION,
};

// Variables and prompt configuration
export {
    IN_PRESENTATION_SEARCH_VARIABLES,
    IN_PRESENTATION_SEARCH_PROMPT_KEYS,
    IN_PRESENTATION_SEARCH_PROMPT_CONFIG,
} from './variables';
